import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '@/app/hooks';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { confirmEmail, resendConfirmation } from '@/services/authService';
import { setAuthCredentials } from '@/features/auth/authSlice';

type Status = 'loading' | 'success' | 'error';
type ResendStatus = 'idle' | 'sending' | 'sent' | 'error';

const EmailConfirmationPage = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [status, setStatus] = useState<Status>('loading');
  const [messageKey, setMessageKey] = useState<string | null>(null);
  const [messageText, setMessageText] = useState<string>('');
  const message = messageKey ? t(messageKey) : messageText;
  const [resendEmail, setResendEmail] = useState('');
  const [resendStatus, setResendStatus] = useState<ResendStatus>('idle');
  const [resendErrorMessage, setResendErrorMessage] = useState<string | null>(null);
  const [resendErrorKey, setResendErrorKey] = useState<string | null>(null);
  const resendErrorText = resendErrorKey ? t(resendErrorKey) : resendErrorMessage;

  useEffect(() => {
    const run = async () => {
      if (!token) {
        setStatus('error');
        setMessageKey('confirmEmail.missingToken');
        setMessageText('');
        return;
      }

      try {
        const response = await confirmEmail(token);
        if (response.user) {
          dispatch(setAuthCredentials(response.user));
        }
        setStatus('success');
        if (response.message) {
          setMessageKey(null);
          setMessageText(response.message);
        } else {
          setMessageKey('confirmEmail.success');
        }
      } catch (error: any) {
        setStatus('error');
        const responseMessage = error.response?.data?.message;
        if (responseMessage) {
          setMessageKey(null);
          setMessageText(responseMessage);
        } else {
          setMessageKey('confirmEmail.invalidToken');
        }
      }
    };

    void run();
  }, [dispatch, token, t]);

  const handleResend = async () => {
    if (!resendEmail || resendStatus === 'sending') {
      return;
    }

    setResendStatus('sending');
    setResendErrorMessage(null);
    setResendErrorKey(null);
    try {
      await resendConfirmation(resendEmail);
      setResendStatus('sent');
    } catch (error: any) {
      setResendStatus('error');
      const responseMessage = error.response?.data?.message;
      if (responseMessage) {
        setResendErrorKey(null);
        setResendErrorMessage(responseMessage);
      } else {
        setResendErrorKey('common.unexpectedError');
      }
    }
  };

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-6 py-16 bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 text-white">
      <div className="w-full max-w-xl rounded-2xl border border-slate-800 bg-slate-900/70 shadow-2xl p-8">
        <h1 className="text-3xl font-semibold mb-4">
          {t('confirmEmail.title')}
        </h1>
        <p className="text-slate-200 mb-6">
          {status === 'loading' && t('confirmEmail.loading')}
          {status !== 'loading' && message}
        </p>

        {status === 'success' && (
          <Link
            to="/studio"
            className="inline-flex items-center justify-center rounded-md bg-indigo-500 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-400 transition-colors"
          >
            {t('confirmEmail.goToLogin')}
          </Link>
        )}

        {status === 'error' && (
          <div className="mt-6 space-y-4">
            <p className="text-slate-300">
              {t('confirmEmail.resendPrompt')}
            </p>
            <div className="flex flex-col gap-3 sm:flex-row">
              <Input
                value={resendEmail}
                onChange={(event) => setResendEmail(event.target.value)}
                placeholder={t('confirmEmail.emailPlaceholder')}
                type="email"
              />
              <Button onClick={handleResend} disabled={resendStatus === 'sending'}>
                {resendStatus === 'sending'
                  ? t('confirmEmail.resending')
                  : t('confirmEmail.resendButton')}
              </Button>
            </div>
            {resendStatus === 'sent' && (
              <p className="text-green-300 text-sm">{t('confirmEmail.resendSuccess')}</p>
            )}
            {resendStatus === 'error' && resendErrorText && (
              <p className="text-red-300 text-sm">{resendErrorText}</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default EmailConfirmationPage;
